import React from "react";
import moment from "moment";

import {
  SimpleGrid,
  ISimpleGridColumns,
  GridStyles,
  debounce,
} from "component-library";
import { dataTestIds } from "data-testids";

import { Credit, CreditHeading } from "models";
import { ICreditsSectionContent } from "./CreditsGridContent";

import "./CreditsGrid.scss";
import { TextDivider } from "shared/modules/Common";

export interface ICreditsGridProps {
  credits: Credit[];
  headings: CreditHeading[];
  content: ICreditsSectionContent;
  rowsAmount: number;
}

declare type GridCreditType = Pick<
  Credit,
  "id" | "name" | "dateFrom" | "typeId" | "role" | "company" | "director"
>;

const CREDITS_GRID: ISimpleGridColumns<GridCreditType> = {
  id: "hide",
  name: {
    type: "string",
    label: "",
    width: 3,
    formatCell: (value: string) => (
      <div className="c-credits-grid__credit-title">{value}</div>
    ),
  },
  dateFrom: {
    type: "string",
    label: "",
    width: 1,
  },
  typeId: {
    type: "string",
    label: "",
    width: 2,
  },
  role: {
    type: "string",
    label: "",
    width: 2,
  },
  company: {
    type: "string",
    label: "",
    width: 2,
  },
  director: {
    type: "string",
    label: "",
    width: 2,
  },
};

export class CreditsGrid extends React.Component<ICreditsGridProps> {
  private readonly gridStyles = CreditsGrid.intiGridStyles();
  private animatedContainerRef = React.createRef<HTMLDivElement>();
  private containerRef = React.createRef<HTMLDivElement>();

  public render() {
    const { credits, rowsAmount } = this.props;
    this.initGridConfig();

    const visibleCredits = credits.slice(0, rowsAmount);

    return (
      <div ref={this.animatedContainerRef} className="c-credits-grid">
        <div ref={this.containerRef} className="c-credits-grid__container">
          <SimpleGrid<GridCreditType>
            data={visibleCredits}
            styles={this.gridStyles}
            columns={CREDITS_GRID}
            maxHeight="10000000px"
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    window.addEventListener("resize", this.adaptGridContainerDebounced, false);
    window.addEventListener(
      "orientationchange",
      this.adaptGridContainerDebounced,
      false
    );
  }

  public componentWillUnmount() {
    window.removeEventListener(
      "resize",
      this.adaptGridContainerDebounced,
      false
    );
    window.removeEventListener(
      "orientationchange",
      this.adaptGridContainerDebounced,
      false
    );
  }

  public componentDidUpdate() {
    this.adaptGridContainer();
  }

  private renderType = (label: string) => (value: any) => {
    const heading = this.props.headings.find(
      (heading) => heading.creditType.id === value
    );

    return this.renderCell(label, heading ? heading.creditType.name : "-");
  };

  private renderDate = (label: string) => (value: any, credit: Credit) => {
    const fromDate = this.formatTime(credit.dateFrom, credit.dateFromFormat);
    const toDate = this.formatTime(credit.dateTo, credit.dateToFormat);

    const isDashVisible = Boolean(fromDate) && Boolean(toDate);

    value = `${fromDate || ""}${isDashVisible ? "-" : ""}${toDate || ""}`;

    return this.renderCell(label, value);
  };

  private formatTime = (date?: Date, granularity?: string) => {
    let format = "DD/MM/YYYY";

    if (granularity === "m") {
      format = "MM/YYYY";
    } else if (granularity === "y") {
      format = "YYYY";
    }

    return date && moment(date).format(format);
  };

  private renderTextCell = (label: string) => (value: any) =>
    this.renderCell(label, value);

  private renderRoleCell = (label: string) => (value: any) =>
    this.renderCell(label, <TextDivider text={value} separator="/" />);

  private renderCell = (label: string, value: any) => {
    return (
      <div className="c-credits-grid__cell">
        <div className="c-credits-grid__cell-label">{label}</div>
        <div
          className="c-credits-grid__cell-value"
          data-testid={dataTestIds.profile["SkillsGrid.SkillsGrid.CellValue"]}
        >
          {value}
        </div>
      </div>
    );
  };

  private initGridConfig = () => {
    const { content } = this.props;
    (CREDITS_GRID.dateFrom as any).label = content.dateColumn;
    (CREDITS_GRID.typeId as any).label = content.typeColumn;
    (CREDITS_GRID.company as any).label = content.companyColumn;
    (CREDITS_GRID.director as any).label = content.directorColumn;
    (CREDITS_GRID.role as any).label = content.roleColumn;
    (CREDITS_GRID.dateFrom as any).formatCell = this.renderDate(
      content.dateColumn
    );
    (CREDITS_GRID.typeId as any).formatCell = this.renderType(
      content.typeColumn
    );
    (CREDITS_GRID.company as any).formatCell = this.renderTextCell(
      content.companyColumn
    );
    (CREDITS_GRID.director as any).formatCell = this.renderTextCell(
      content.directorColumn
    );
    (CREDITS_GRID.role as any).formatCell = this.renderRoleCell(
      content.roleColumn
    );
  };

  private static intiGridStyles = () => {
    const gridStyles = new GridStyles();
    gridStyles.body = "c-credits-grid__body";
    gridStyles.bodyCell = "c-credits-grid__body-cell";
    gridStyles.bodyRow = "c-credits-grid__body-row";
    gridStyles.header = "c-credits-grid__header";
    gridStyles.headerCell = "c-credits-grid__header-cell";
    gridStyles.headerLabel = "c-credits-grid__header-label";
    gridStyles.headerRow = "c-credits-grid__header-row";
    gridStyles.headerTitle = "c-credits-grid__header-title";

    return gridStyles;
  };

  private adaptGridContainer = () => {
    const { current: container } = this.containerRef;
    const { current: animatedContainer } = this.animatedContainerRef;

    if (container && animatedContainer) {
      animatedContainer.style.maxHeight = container.offsetHeight + "px";
      animatedContainer.style.minHeight = container.offsetHeight + "px";
    }
  };

  private adaptGridContainerDebounced = debounce(this.adaptGridContainer, 200);
}
